import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, OnDestroy, PLATFORM_ID, Renderer2, RendererFactory2 } from '@angular/core';

import { from, Observable, of, Subscription } from 'rxjs';

import { PostMessenger, SentryLogger, WindowRef } from '@fcom/core';
import { unsubscribe } from '@fcom/core/utils';
import { FinnairOrder } from '@fcom/dapi/api/models';
import { LanguageService } from '@fcom/ui-translate';
import {
  getCarTrawlerDateTime,
  getCarTrawlerLang,
  supportedCarTrawlerCurrencies,
  ThirdPartyAncillariesProvider,
} from '@fcom/common/utils';

import { CarTrawlerClientID, carTrawlerSmartBlockConfig } from '../../constants';

@Injectable({
  providedIn: 'root',
})
export class CarTrawlerWidgetService implements OnDestroy {
  private iframeElementId = 'cartrawler-widget-iframe';
  private renderer: Renderer2;
  private DEFAULT_STAY = 7;

  iframeElement: HTMLIFrameElement;

  subscriptions = new Subscription();

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private sentryLogger: SentryLogger,
    private languageService: LanguageService,
    rendererFactory: RendererFactory2,
    private windowRef: WindowRef,
    @Inject(PLATFORM_ID) private platform: object
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  ngOnDestroy(): void {
    this.cleanup();
    unsubscribe(this.subscriptions);
  }

  public initWidget(parentElementId: string, order: FinnairOrder, clientId: CarTrawlerClientID): Observable<boolean> {
    return this.addIframeElement(parentElementId, order, clientId);
  }

  private addIframeElement(
    parentElementId: string,
    order: FinnairOrder,
    clientId: CarTrawlerClientID
  ): Observable<boolean> {
    if (this.document.getElementById(this.iframeElementId)) {
      return of(true);
    }

    this.iframeElement = this.renderer.createElement('iframe');

    this.iframeElement.setAttribute('id', this.iframeElementId);
    this.iframeElement.setAttribute('ct-product', 'widget');
    this.iframeElement.setAttribute(
      'sandbox',
      'allow-same-origin allow-scripts allow-popups allow-popups-to-escape-sandbox allow-forms'
    );
    this.iframeElement.setAttribute('frameborder', '0');
    this.iframeElement.setAttribute('scrolling', 'no');
    this.iframeElement.setAttribute('width', '100%');
    this.iframeElement.setAttribute(
      'style',
      'border: 0; width: 100%; max-height: 100%; display: block; position: relative;'
    );

    const firstFlightArrivalDateTime = getCarTrawlerDateTime(order?.bounds[0].arrival.dateTime, 1);
    const firstFlightDepartureDateTime = getCarTrawlerDateTime(order?.bounds[0].departure.dateTime, 1);

    const queryParams = {
      currency: supportedCarTrawlerCurrencies(order?.prices?.included?.total?.total?.totalAmount?.currencyCode),
      language: getCarTrawlerLang(ThirdPartyAncillariesProvider.CAR_TRAWLER_SMARTBLOCK, this.languageService.langValue),
      'ct-product': carTrawlerSmartBlockConfig.CTProduct,
      'flight-0-arrival-date-time': firstFlightArrivalDateTime,
      'flight-0-arrival-iata': order.bounds[0].arrival.locationCode,
      'flight-0-departure-date-time': firstFlightDepartureDateTime,
      'flight-0-departure-iata': order.bounds[0].departure.locationCode,
      pax: String(order.passengers.length),
    };

    if (order.bounds[1]) {
      const secondFlightArrivalDateTime = getCarTrawlerDateTime(order?.bounds[1].arrival.dateTime, -1);
      const secondFlightDepartureDateTime = getCarTrawlerDateTime(order?.bounds[1].departure.dateTime, -1);

      queryParams['flight-1-arrival-date-time'] = secondFlightArrivalDateTime;
      queryParams['flight-1-arrival-iata'] = order.bounds[1].arrival.locationCode;
      queryParams['flight-1-departure-date-time'] = secondFlightDepartureDateTime;
      queryParams['flight-1-departure-iata'] = order.bounds[1].departure.locationCode;
    } else {
      queryParams['flight-1-departure-iata'] = order.bounds[0].arrival.locationCode;
      queryParams['flight-1-departure-date-time'] = getCarTrawlerDateTime(
        order?.bounds[0].arrival.dateTime,
        24 * this.DEFAULT_STAY + 1
      );
    }

    const cartrawlerOrigin = 'https://product-router.cartrawler.com';

    this.iframeElement.setAttribute(
      'src',
      `${cartrawlerOrigin}/product/widget/${carTrawlerSmartBlockConfig.partnerName}/${clientId.toString()}/${getCarTrawlerLang(ThirdPartyAncillariesProvider.CAR_TRAWLER_SMARTBLOCK, this.languageService.langValue)}?${new URLSearchParams(queryParams).toString()}`
    );

    this.renderer.appendChild(this.document.querySelector(`#${parentElementId}`), this.iframeElement);

    const messenger = new PostMessenger(this.windowRef, of([cartrawlerOrigin]), this.platform);

    messenger.inbound$.subscribe((msg: string) => {
      const [_data, height, _width, _event] = msg.split(':');

      if (height) {
        this.iframeElement.style.height = height + 'px';
      }
    });

    return from(
      new Promise<boolean>((resolve) => {
        this.iframeElement.onload = () => {
          this.iframeElement.contentWindow.postMessage(
            '[iFrameSizer]cartrawler-widget-iframe:8:false:false:32:true:true:null:documentElementOffset:null:null:0:false:child:scroll:true',
            '*'
          );
          resolve(true);
        };

        this.iframeElement.onerror = (error) => {
          this.sentryLogger.error('Error from CarTrawler iframe', error);
          resolve(false);
        };
      })
    );
  }

  cleanup(): void {
    this.iframeElement.onload = null;
    this.iframeElement.onerror = null;
  }
}
